import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportAPI, SessionInfo } from '../../../services/api';
import { ReportTarget } from '../../../types/api';
import { prefetchReportTargets } from '../utils/reportTargetCache';
import { clearFrontendSessionState, OAUTH_REDIRECT_IN_PROGRESS_KEY } from '../../../utils/sessionCleanup';

const SESSION_SYNC_INTERVAL_MS = 60 * 1000;
const SPIRIS_MISSING_NOTICE = 'Spiris saknas. Anslut.';

type ResolvedSessionState = {
  allAccess: boolean;
  adminAccess: boolean;
  resolvedTarget: ReportTarget | null;
  resolvedProfileKey: string;
  tokenStatus: string;
  needsReconnect: boolean;
};

const areReportTargetsEqual = (left: ReportTarget | null, right: ReportTarget | null) => {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return left.id === right.id
    && left.type === right.type
    && left.label === right.label
    && left.description === right.description
    && left.memberCount === right.memberCount;
};

export const useRouteAuthenticatedUser = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [spirisConnectionNotice, setSpirisConnectionNotice] = useState<string | null>(null);
  const [isAllAccess, setIsAllAccess] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentTarget, setCurrentTarget] = useState<ReportTarget | null>(null);
  const [currentProfileKey, setCurrentProfileKey] = useState('');
  const [isOauthRedirectInProgress, setIsOauthRedirectInProgress] = useState(() => sessionStorage.getItem(OAUTH_REDIRECT_IN_PROGRESS_KEY) === '1');
  const [profileReloadKey, setProfileReloadKey] = useState(0);
  const hasInitializedAuthRef = useRef(false);
  const sessionSyncInFlightRef = useRef(false);

  const navigateIfNeeded = useCallback((to: string, replace = false) => {
    const currentLocation = `${window.location.pathname}${window.location.search}`;
    if (currentLocation !== to) {
      navigate(to, { replace });
    }
  }, [navigate]);

  const clearClientAuthState = useCallback(() => {
    clearFrontendSessionState();
    setIsAuthenticated(false);
    setIsAllAccess(false);
    setIsAdmin(false);
    setCurrentTarget(null);
    setCurrentProfileKey('');
    setSpirisConnectionNotice(null);
  }, []);

  const applyAuthenticatedSessionInfo = useCallback((info: SessionInfo): ResolvedSessionState => {
    const allAccess = Boolean(info.allAccess);
    const adminAccess = Boolean(info.isAdmin);
    const resolvedTarget = info.target ?? null;
    const resolvedProfileKey = (info.profileKey || '').trim();
    const tokenStatus = (info.tokenStatus || '').toLowerCase();
    const needsReconnect = tokenStatus === 'token_missing_or_needs_reconnect';

    setIsAuthenticated(true);
    setIsAllAccess(allAccess);
    setIsAdmin(adminAccess);
    setCurrentTarget((prev) => (areReportTargetsEqual(prev, resolvedTarget) ? prev : resolvedTarget));
    setCurrentProfileKey(resolvedProfileKey);
    setSpirisConnectionNotice(needsReconnect ? SPIRIS_MISSING_NOTICE : null);

    if ((allAccess || adminAccess) && !needsReconnect) {
      void prefetchReportTargets().catch(() => {});
    }

    return {
      allAccess,
      adminAccess,
      resolvedTarget,
      resolvedProfileKey,
      tokenStatus,
      needsReconnect,
    };
  }, []);

  const forceLogout = useCallback(async (message?: string, callServer = false) => {
    if (callServer) {
      try {
        await reportAPI.logout();
      } catch {
      }
    }

    clearClientAuthState();
    if (message) {
      localStorage.setItem('auth_error', message);
    }
    navigateIfNeeded('/login', true);
  }, [clearClientAuthState, navigateIfNeeded]);

  const syncAuthenticatedSession = useCallback(async () => {
    if (sessionSyncInFlightRef.current) {
      return;
    }

    sessionSyncInFlightRef.current = true;

    try {
      const info = await reportAPI.getSessionInfo();

      if (!info.authenticated) {
        await forceLogout(undefined, false);
        return;
      }

      const { needsReconnect } = applyAuthenticatedSessionInfo(info);

      if (needsReconnect) {
        navigateIfNeeded('/admin', true);
        return;
      }
    } catch {
    } finally {
      sessionSyncInFlightRef.current = false;
    }
  }, [applyAuthenticatedSessionInfo, forceLogout, navigateIfNeeded]);

  const handleOpenReportForProfile = useCallback((profileKey: string) => {
    setCurrentTarget((prev) => {
      const next = { id: profileKey, type: 'company' as const, label: profileKey };
      return areReportTargetsEqual(prev, next) ? prev : next;
    });
    setCurrentProfileKey(profileKey);
    setProfileReloadKey((value) => value + 1);
    navigateIfNeeded('/report');
  }, [navigateIfNeeded]);

  const handleReportTargetSelected = useCallback((target: ReportTarget) => {
    sessionStorage.removeItem('company_switch_in_progress');
    setCurrentTarget((prev) => (areReportTargetsEqual(prev, target) ? prev : target));
    setCurrentProfileKey(target.type === 'company' ? target.id : '');
    setProfileReloadKey((value) => value + 1);
    navigateIfNeeded('/report');
  }, [navigateIfNeeded]);

  const handleSwitchCompany = useCallback(() => {
    sessionStorage.setItem('company_switch_in_progress', '1');
    navigateIfNeeded('/select');
  }, [navigateIfNeeded]);

  const handleManageSpirisConnections = useCallback(() => {
    navigateIfNeeded('/admin');
  }, [navigateIfNeeded]);

  useEffect(() => {
    if (hasInitializedAuthRef.current) {
      return;
    }
    hasInitializedAuthRef.current = true;

    const initAuth = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const path = window.location.pathname.toLowerCase();
        const wantsAdmin = path.startsWith('/admin');
        const wantsLogin = path.startsWith('/login');
        const wantsSelect = path.startsWith('/select');
        const wantsReportResult = path.startsWith('/report/result');

        const authError = (params.get('error') || '').toLowerCase();
        if (authError === 'no_profile_match') {
          clearClientAuthState();
          navigateIfNeeded('/no-access', true);
          return;
        }

        const info = await reportAPI.getSessionInfo();

        if (!info.authenticated) {
          localStorage.removeItem('auth_state');
          clearClientAuthState();
          if (info.error === 'no_profile_match' || info.error === 'unauthorized_user') {
            navigateIfNeeded('/no-access', true);
          } else {
            navigateIfNeeded(wantsAdmin ? '/admin' : '/login', true);
          }
          return;
        }

        localStorage.setItem('auth_state', 'authenticated');

        const {
          allAccess,
          adminAccess,
          resolvedTarget,
          needsReconnect,
        } = applyAuthenticatedSessionInfo(info);

        if (adminAccess && !wantsAdmin && !needsReconnect) {
          reportAPI.listAdminProfiles()
            .then((adminProfiles) => {
              const profileCount = adminProfiles?.profiles?.length || 0;
              if (profileCount === 0) {
                setSpirisConnectionNotice('Inga Spiris-profiler hittades. Lägg till en koppling.');
              }
            })
            .catch(() => {});
        }

        const postLogin = (localStorage.getItem('post_login_page') || '').toLowerCase();
        const postSpiris = (localStorage.getItem('post_spiris_connect_page') || '').toLowerCase();
        const goAdmin = wantsAdmin || postLogin === 'admin' || postSpiris === 'admin' || needsReconnect;
        const hasSelectedTarget = Boolean(resolvedTarget?.id);
        const switchInProgress = sessionStorage.getItem('company_switch_in_progress') === '1';
        const mustChooseCompany = (allAccess || adminAccess) && !hasSelectedTarget;

        const shouldAutoEnter =
          wantsAdmin ||
          needsReconnect ||
          postLogin === 'admin' ||
          postLogin === 'main' ||
          postSpiris === 'admin' ||
          !wantsLogin;

        localStorage.removeItem('post_login_page');
        localStorage.removeItem('post_spiris_connect_page');

        if (!shouldAutoEnter) {
          navigateIfNeeded('/login', true);
          return;
        }

        const shouldGoToSelect =
          wantsSelect ||
          switchInProgress ||
          postLogin === 'main' ||
          (mustChooseCompany && !goAdmin);

        if (shouldGoToSelect) {
          navigateIfNeeded('/select', true);
          return;
        }

        if (goAdmin) {
          navigateIfNeeded('/admin', true);
        } else {
          navigateIfNeeded(wantsReportResult ? '/report/result' : '/report', true);
        }
      } catch (err) {
        const message = (err as { userMessage?: string } | null)?.userMessage;
        clearClientAuthState();
        if (message) {
          localStorage.setItem('auth_error', message);
        }
        navigateIfNeeded('/login', true);
      } finally {
        sessionStorage.removeItem(OAUTH_REDIRECT_IN_PROGRESS_KEY);
        setIsOauthRedirectInProgress(false);
        setIsLoading(false);
      }
    };

    void initAuth();
  }, [applyAuthenticatedSessionInfo, clearClientAuthState, navigateIfNeeded]);

  useEffect(() => {
    if (!isAuthenticated || isLoading || isOauthRedirectInProgress) {
      return;
    }

    const handleWindowFocus = () => {
      void syncAuthenticatedSession();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void syncAuthenticatedSession();
      }
    };

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void syncAuthenticatedSession();
      }
    }, SESSION_SYNC_INTERVAL_MS);

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, isLoading, isOauthRedirectInProgress, syncAuthenticatedSession]);

  const handleLogout = useCallback(async () => {
    await forceLogout(undefined, true);
  }, [forceLogout]);

  return {
    isAuthenticated,
    isLoading,
    spirisConnectionNotice,
    isAllAccess,
    isAdmin,
    currentTarget,
    currentProfileKey,
    profileReloadKey,
    clearClientAuthState,
    handleOpenReportForProfile,
    handleReportTargetSelected,
    handleSwitchCompany,
    handleManageSpirisConnections,
    handleLogout,
  };
};
